import { connect } from 'pwa-helpers';
import { html, nothing } from "lit";
import { customElement, property, state } from 'lit/decorators.js';
import '../../product-card/product-card-display';


import { ifDefined } from 'lit-html/directives/if-defined.js';
import { BaseComponent } from '../../base-component/base-component';
import store from '../../../store/store';
import { isCustomISBN, isSuccess, selectRequest, selectAdjustedTotal, selectSelectedCatalog } from '../../search/store/search.selector';
import { StoreState } from '../../../store/store.interface';
import { createMessage, getCatalogLabel } from '../../../common/utils/util';
import { booleanConverter } from '../../../common/converters';


/**
 * An element to display the current page within the search results.
 * 
 * @csspart first - The <span> containing the number representing the index of the first result.
 * @csspart last - The <span> containing the number representing the index of the last result.
 * @csspart total - The <span> containing the number representing the total number of results.
 * @csspart term - The <span> containing the number representing the search term.
 */
@customElement('mhecomm-pagination-message')
export class PaginationMessage extends connect(store)(BaseComponent) {

  /**
   * The message to display to the user when there are results. The following placeholders are available: %first% %last% %total% %term%
   */
  @property()
  message = 'Showing %first%-%last% of %total% Results';

  /**
   * The message should be displayed without the compiled version.
   * Use what is being passed on the property value.
   */
  @property({type: Boolean, converter: booleanConverter})
  useRawMessage = false;

  /**
   * The message to display to the user when there are no results. The following placeholders are available: %first% %last% %total% %term%
   */
  @property()
  emptyMessage = '0 Results Matching "%term%"';

  /**
   * The message to display to the user when no term was provided by a %term% appears in the message property.
   */
  @property()
  noTermMessage = 'Showing %first%-%last% of %total% Results';

  /**
   * Region Id should be '-' seperated as this will be used as region name.
   * Example: results-bar will be labeled as 'results bar'
   */
  @property()
  regionid = '';

  @property({ type: Boolean, converter: booleanConverter })
  useAriaLive: boolean = true;

  @state()
  private compiledMessage: HTMLElement | undefined;

  @state()
  private compiledEmptyMessage: HTMLElement | undefined;

  private readonly onSkipToResults = (e: Event) => this.focusRegion(e);

  @state()
  isCustomISBN = false;

  @state()
  private srMessageAnnouncement: string | undefined;

  // Zero-width space — invisible and unspoken by screen readers
  private readonly SILENCE_CHAR = '\u200B';
  private _lastAnnouncedBaseMessage: string = '';
  private _silenceToggle: boolean = false;

  renderComp() {
    return html`
    <style>
      [part="term"], [part="business-unit"] {
        font-weight: bold;
      }
    </style>

    <div tabindex=${ifDefined(this.regionid ? "-1" : undefined)} role=${ifDefined(this.regionid ? "region" : undefined)} aria-label=${ifDefined(this.regionid ? this.regionid.split('-').join(' ') : undefined)} data-automation-id=${ifDefined(this.regionid ? this.regionid : undefined)} id=${ifDefined(this.regionid ? this.regionid : undefined)} class="${this.isCustomISBN ? 'd-none': ''}">
      ${!this.regionid && this.useAriaLive ? html`<span class="visually-hidden" aria-live="polite" aria-atomic="true" role="status">${this.useRawMessage ? this.message : this.srMessageAnnouncement}</span>` : nothing}
        <div>
          ${ this.useRawMessage ? this.message : ''}
          ${ this.compiledMessage && !this.useRawMessage ? this.compiledMessage : ''}
          ${ this.compiledEmptyMessage && !this.useRawMessage ? this.compiledEmptyMessage : ''}
        </div>
    </div>
    `;
  }

  update(changedProperties: any) {
    super.update(changedProperties);
  }

  stateChanged(state: StoreState) {
    if (isSuccess(state)) {
      this.rebuildMessages(state);
        const baseMessage = this.compiledMessage?.textContent
          ?? this.compiledEmptyMessage?.textContent
          ?? '';
        this.srMessageAnnouncement = this._buildAnnouncementText(baseMessage);
    }
  }

  /**
   * Builds the text to set on the aria-live announcement region.
   * When the incoming message is identical to the last announced text, the silence
   * character (\u200B) is toggled on/off forcing the screen reader to re-read the region even when the message content is the same.
   * @param baseMessage The plain message text (without any silence character).
   * @returns The string to assign to `srMessageAnnouncement`.
   */
  private _buildAnnouncementText(baseMessage: string): string {
    if (baseMessage === this._lastAnnouncedBaseMessage) {
      this._silenceToggle = !this._silenceToggle;
      return this._silenceToggle ? `${baseMessage}${this.SILENCE_CHAR}` : baseMessage;
    }
    this._silenceToggle = false;
    this._lastAnnouncedBaseMessage = baseMessage;
    return baseMessage;
  }

  private rebuildMessages(state: StoreState) {
    // Use the adjusted total which automatically subtracts blocked count from Redux store
    const total = selectAdjustedTotal(state);
    const request = selectRequest(state) ?? undefined;
    const selectedCatalog = selectSelectedCatalog(state);
    const term = request?.searchTerms ?? '';
    const start = request?.start ? request.start : 1;
    const maxEnd = (request?.start ? request.start : 1) + (request?.pageLength ? request?.pageLength - 1 : 0);
    const end = maxEnd > total ? total : maxEnd;

    if (total > 0) {
      this.compiledEmptyMessage = undefined;
        if (!term && this.message.includes('%term%')) {
            this.compiledMessage = this.compileMessage(this.noTermMessage, start, end, total, term, selectedCatalog);
        } else {
            this.compiledMessage = this.compileMessage(this.message, start, end, total, term, selectedCatalog);
        }
    } else {
      this.compiledMessage = undefined;
      this.compiledEmptyMessage = this.compileMessage(this.emptyMessage, start, end, total, term, selectedCatalog);
    }
    this.isCustomISBN = isCustomISBN(state);
  }

  connectedCallback(): void {
    super.connectedCallback();
    if (this.regionid) {
      this.addTrackedEventListener(window, 'skip-to-results', this.onSkipToResults);
    }
  }

  compileMessage(message: string, start: number, end: number, total: number, term: string, businessUnit?: string) {
    const values = {
      'first': start.toLocaleString(),
      'last': end.toLocaleString(),
      'total': total.toLocaleString(),
      'term': term,
      'business-unit': businessUnit ? getCatalogLabel(businessUnit) : ''
    };
    
    return createMessage(message, values);
  }

  focusRegion(e) {
    const event = e as CustomEvent;
    if (this.regionid === event.detail.target) {
      this.shadowRoot?.getElementById(this.regionid)?.focus();
    }
    
  }
  
  disconnectedCallback(): void {
    super.disconnectedCallback();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-pagination-message': PaginationMessage;
  }
}
